import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, User, LogOut, Menu, X, KeyRound, Zap, MessageSquare, HelpCircle } from 'lucide-react';
import { useTour } from '../../../tours/useTour';
import { Cycle, cycleService, notificationService, UserNotification } from '../services/adminAuth';
import { communicationService } from '../services/communicationService';
import { useAuth } from '../../../auth/hooks/auth';
import signalRService from '../../../services/signalRService';

const CHIME_URL = "https://assets.mixkit.co/active_storage/sfx/2357/2357-preview.mp3";

interface HeaderProps {
  sidebarCollapsed?: boolean;
  isMobile?: boolean;
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  sidebarCollapsed = false,
  isMobile = false,
  onMenuClick
}) => {
  const { user, logout, triggerAccountSwitch, unreadMessages, refreshUnread } = useAuth();
  const { startTour } = useTour();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);
  const [showCycleDropdown, setShowCycleDropdown] = useState(false);
  const [runningCycle, setRunningCycle] = useState<Cycle | null>(null);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('Disconnected');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const cycleDropdownRef = useRef<HTMLDivElement>(null);

  const userImage = null;
  const isAuthor = user?.role === 'author';
  const notificationsPath = isAuthor ? '/author/notifications' : '/admin/notifications';
  const isDESSOrTagChairman = user?.roleId === 9 || user?.roleId === 5 || user?.role === 'dess';

  const unreadCount = notifications.filter(n => !n.messageRead).length;
  const totalUnread = unreadCount + (isDESSOrTagChairman ? (unreadMessages?.length || 0) : 0);

  const fetchBarNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const res = await notificationService.getUserNotifications(true);
      if (res.isSuccess) setNotifications(res.data);
    } catch (err) {
      } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    fetchBarNotifications();
  }, []);

  const handleOpenNotifications = () => {
    setShowNotifications(!showNotifications);
    setShowDropdown(false);
    if (!showNotifications) {
      fetchBarNotifications();
    }
  };

  const handleNotificationClick = async (notification: UserNotification) => {
    if (!notification.messageRead) {
      try {
        await notificationService.markMessageAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, messageRead: true } : n)
        );
      } catch (err) {
        }
    }
    setShowNotifications(false);
    navigate(notificationsPath, { state: { openNotificationId: notification.id } });
  };

  const handleInternalMessageClick = async (msg: any) => {
    if (!msg.submissionId) return;
    setMarkingAsRead(msg.submissionId);
    try {
      // Try marking as read, but don't block navigation if it fails (e.g. 405 error)
      try {
        await communicationService.markAsRead(msg.submissionId);
      } catch (markErr) {
        }

      if (refreshUnread) await refreshUnread();
      setShowNotifications(false);
      navigate(`/admin/prima-facie-reports?openChat=${msg.submissionId}`);
    } catch (err) {
      } finally {
      setMarkingAsRead(null);
    }
  };

  const handleSeeMore = () => {
    setShowNotifications(false);
    navigate(notificationsPath);
  };

  useEffect(() => {
    const fetchRunningCycle = async () => {
      try {
        const response = await cycleService.getRunningCycle();
        if (response?.isSuccess && response?.data) {
          setRunningCycle(response?.data);
        }
      } catch (error) {
        }
    };
    fetchRunningCycle();

    const handleActiveCycleUpdated = () => fetchRunningCycle();
    window.addEventListener('activeCycleUpdated', handleActiveCycleUpdated);
    return () => window.removeEventListener('activeCycleUpdated', handleActiveCycleUpdated);
  }, []);

  useEffect(() => {
    const unsub = signalRService.onStateChange((status) => {
      setConnectionStatus(status);
    });
    return () => unsub();
  }, []);

  // Real-time notification listener for the badge
  useEffect(() => {
    const playChime = () => {
      try {
        const audio = new Audio(CHIME_URL);
        audio.volume = 0.5; // Set to 50% volume
        audio.play().catch(() => {
          // Silent catch - browser blocked autoplay
          });
      } catch (err) {
        }
    };

    const unsubscribe = signalRService.onReceiveMessage(async (data) => {
      // Play sound
      playChime();

      // 1. Refresh the unread counts in AuthContext (for the red badge)
      if (refreshUnread) {
        await refreshUnread();
      }

      // 2. Refresh the notification dropdown list
      await fetchBarNotifications();
    });

    return () => unsubscribe();
  }, [refreshUnread]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Keep the dropdown open while the tour highlights items inside it.
      if (target.closest('[class*="driver-"]')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setShowDropdown(false);
      }
      if (cycleDropdownRef.current && !cycleDropdownRef.current.contains(target)) {
        setShowCycleDropdown(false);
      }
    };
    if (showDropdown || showCycleDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown, showCycleDropdown]);

  const truncate = (str: string, max: number) =>
    str && str.length > max ? str.slice(0, max) + '…' : str;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className={`fixed top-0 right-0 flex justify-between items-center bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 z-40 transition-all duration-300 ${isMobile ? 'left-0' : sidebarCollapsed ? 'left-[5.4rem]' : 'left-64'
      }`}>
      {/* Left Section */}
      <div className="flex items-center gap-3 flex-grow">
        {isMobile && (
          <button onClick={onMenuClick} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg lg:hidden">
            <Menu className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2 sm:gap-4 lg:gap-6">
        {/* Connectivity Status */}
        <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 bg-gray-50 border border-gray-100 rounded-full" title={connectionStatus}>
          <div className={`w-2 h-2 rounded-full ${
             connectionStatus === 'Connected' ? 'bg-green-500' : 
             connectionStatus === 'Connecting' ? 'bg-yellow-500 animate-pulse' : 'bg-red-500'
          }`} />
          {connectionStatus === 'Connecting' && (
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                Connecting...
              </span>
          )}
        </div>

        {/* Cycle Badge - Desktop */}
        {runningCycle && (
          <div className="hidden md:flex items-center gap-2">
            <span className="px-3 lg:px-4 py-1 bg-[#DCFCE7] text-[#016630] rounded-full text-xs lg:text-sm font-semibold border border-[#B9F8CF] whitespace-nowrap">
              Cycle: {runningCycle?.name}
            </span>
          </div>
        )}

        {/* Guide Button */}
        <button
          id="tour-header-guide"
          onClick={startTour}
          title="App Guide"
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors group"
        >
          <HelpCircle className="w-5 h-5 text-gray-600 group-hover:text-[#016630]" />
        </button>

        {/* Notification Bell */}
        <div id="tour-header-bell" className="relative cursor-pointer p-1.5 hover:bg-gray-100 rounded-lg transition-colors group" onClick={handleOpenNotifications}>
          <Bell className="w-5 h-5 text-gray-600 group-hover:text-[#016630]" />
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white border-2 border-white">
              {totalUnread}
            </span>
          )}
        </div>

        {/* User Section */}
        <div className="relative pl-3 sm:pl-6 border-l border-gray-300" ref={dropdownRef}>
          <button id="tour-header-welcome" className="flex items-center gap-2 sm:gap-3" type="button" onClick={() => setShowDropdown(!showDropdown)}>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center border-2 border-blue-200 text-blue-600 font-bold text-xs sm:text-sm">
              {user?.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) || 'U'}
            </div>
            <div className="text-right hidden sm:block">
              <div className="font-semibold text-sm text-gray-900">{user?.name || 'User'}</div>
              <div className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                {user?.displayRole || (user?.role !== 'author' ? 'Administrator' : 'Author')}
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400 hidden sm:block" />
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100">
                  <div>
                    <div className="font-semibold text-sm text-gray-900">{user?.name || 'User'}</div>
                    <div className="text-xs text-gray-500">{user?.email || ''}</div>
                  </div>
                </div>
                <Link id="tour-header-profile" to={user?.role === 'author' ? '/author/profile' : '/admin/profile'} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium transition-colors" onClick={() => setShowDropdown(false)}>
                  <User className="w-4 h-4" />
                  <span>View Profile</span>
                </Link>
                {Object.keys(user?.allRoles || {}).length > 1 && (
                  <button onClick={() => { triggerAccountSwitch(); setShowDropdown(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium transition-colors">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span>Switch Account</span>
                  </button>
                )}
                <Link
                  id="tour-header-change-password"
                  to={
                    user?.role === 'author' ? '/author/change-password' : '/admin/change-password'
                  }
                  className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium transition-colors"
                  onClick={() => setShowDropdown(false)}
                >
                  <KeyRound className="w-4 h-4" />
                  <span>Change Password</span>
                </Link>
                <button id="tour-header-signout" onClick={() => { logout(); setShowDropdown(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-md text-sm font-medium transition-colors">
                  <LogOut className="w-4 h-4" />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Unified Notification Drawer */}
      {showNotifications && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setShowNotifications(false)} />
          <div className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
            <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100 bg-gray-50/50">
              <div>
                <h5 className="font-bold text-gray-900">Notifications</h5>
                {totalUnread > 0 && <p className="text-[10px] font-bold text-[#016630] uppercase tracking-wider mt-0.5">{totalUnread} Unread Items</p>}
              </div>
              <button onClick={() => setShowNotifications(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-white">
              {notificationsLoading ? (
                <div className="p-4 space-y-4">
                  {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-50 animate-pulse rounded-xl" />)}
                </div>
              ) : (unreadMessages.length === 0 && notifications.length === 0) ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
                  <Bell className="w-12 h-12 text-gray-200" />
                  <p className="text-sm font-bold text-gray-900">No new notifications</p>
                </div>
              ) : (
                <div className="p-3 space-y-4">
                  {/* Internal Discussions */}
                  {isDESSOrTagChairman && unreadMessages.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 px-1">
                        <MessageSquare className="w-3.5 h-3.5 text-[#016630]" />
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Internal Discussions</span>
                      </div>
                      {unreadMessages.map(msg => (
                        <button key={msg.id} disabled={!!markingAsRead} onClick={() => handleInternalMessageClick(msg)} className="w-full text-left p-4 rounded-2xl border border-blue-100 bg-blue-50/30 hover:bg-blue-50 transition-all group relative">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold flex-shrink-0 border border-blue-200">
                              {msg.senderName?.charAt(0) || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center mb-0.5">
                                <span className="text-sm font-bold text-gray-900 truncate">{msg.senderName}</span>
                                <span className="text-[10px] text-gray-400">{formatDate(msg.sendDateTime)}</span>
                              </div>
                              <p className="text-xs text-gray-600 line-clamp-1 italic">"{msg.message}"</p>
                            </div>
                          </div>
                          {markingAsRead === msg.submissionId && (
                            <div className="absolute inset-0 bg-white/40 flex items-center justify-center rounded-2xl">
                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* General Notifications */}
                  {notifications.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 px-1">
                        <Bell className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">General Notifications</span>
                      </div>
                      {notifications.map(n => (
                        <button key={n.id} onClick={() => handleNotificationClick(n)} className={`w-full text-left p-4 rounded-xl border transition-all ${!n.messageRead ? 'bg-gray-50/50 border-gray-200 shadow-sm' : 'bg-white border-gray-100'}`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${!n.messageRead ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>
                              {n.subject?.[0] || 'N'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center mb-0.5">
                                <span className="text-sm font-medium text-gray-900 truncate">{n.subject}</span>
                                <span className="text-[10px] text-gray-400">{formatDate(n.dateSent || n.dateCreated)}</span>
                              </div>
                              <p className="text-[11px] text-gray-500 line-clamp-1"><b>To:</b> {n.to}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-100">
              <button onClick={handleSeeMore} className="w-full py-2.5 text-xs font-bold text-[#016630] hover:bg-[#DCFCE7] rounded-lg transition-colors border border-[#B9F8CF] uppercase tracking-widest">
                See All Notifications
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Header;